"use client";

import { Label } from "@/components/atoms/Label";
import { cn } from "@/lib/utils";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";

export type RaddioCircleProps = {
  className?: string;
  disabled?: boolean;
};
const RadioCircle = ({ className, disabled }: RaddioCircleProps) => {
  return (
    <div
      className={cn(
        "tw-size-12 tw-rounded-circular tw-bg-gray-900 radio-group-item-circle",
        { "tw-bg-gray-400": disabled },
        className,
      )}
    />
  );
};

/**
 * The context provider makes the group props
 * available to all radio group items and wrappers
 */
const RadioGroupContext = React.createContext(
  {} as {
    value: string | undefined;
    onValueChange: ((value: string) => void) | undefined;
    variant?: "default" | "box";
    disabled?: boolean;
    errorMsg?: string;
  },
);
const RadioGroupContextProvider = ({
  children,
  value,
  onValueChange,
  variant,
  disabled,
  errorMsg,
}: React.PropsWithChildren<{
  value?: string;
  onValueChange?: (value: string) => void;
  variant?: "default" | "box";
  disabled?: boolean;
  errorMsg?: string;
}>) => {
  return (
    <RadioGroupContext.Provider
      value={{
        value,
        onValueChange,
        variant,
        disabled,
        errorMsg,
      }}
    >
      {children}
    </RadioGroupContext.Provider>
  );
};
const useRadioGroupContext = () => {
  const context = React.useContext(RadioGroupContext);
  if (!context) {
    throw new Error(
      "useRadioGroupContext must be used within a RadioGroupContextProvider",
    );
  }
  return context;
};

export type RadioGroupProps = RadioGroupPrimitive.RadioGroupProps & {
  testId?: string;
  errorMsg?: string;
  variant?: "default" | "box";
};
const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className,
      testId,
      children,
      errorMsg,
      value,
      onValueChange,
      variant = "default",
      ...props
    },
    ref,
  ) => {
    return (
      <RadioGroupContextProvider
        value={value}
        onValueChange={onValueChange}
        variant={variant}
        disabled={props.disabled}
        errorMsg={errorMsg}
      >
        <RadioGroupPrimitive.Root
          ref={ref}
          data-testid={testId}
          className={cn(
            "tw-grid tw-gap-16",
            {
              "tw-pointer-events-none [&_.radio-group-item-box]:tw-border-gray-400 [&_.radio-group-item]:tw-border-gray-400 [&_.radio-group-item-text]:tw-text-gray-500 [&_.radio-group-item-circle]:tw-bg-gray-500":
                props.disabled,
            },
            className,
          )}
          value={value}
          onValueChange={onValueChange}
          {...props}
        >
          {children}
        </RadioGroupPrimitive.Root>
      </RadioGroupContextProvider>
    );
  },
);
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

export type RadioGroupItemProps = RadioGroupPrimitive.RadioGroupItemProps & {
  label?: React.ReactNode;
  note?: React.ReactNode;
  errorMsg?: string;
  allowEventBubbling?: boolean;
};
const RadioGroupItem = React.forwardRef<HTMLButtonElement, RadioGroupItemProps>(
  ({ className, label, note, value, disabled, allowEventBubbling, ...props }, ref) => {
    const itemRef = React.useRef<HTMLButtonElement>(null);

    const {
      variant,
      value: radioGroupValue,
      disabled: radioGroupDisabled,
      errorMsg: radioGroupErrorMsg,
      onValueChange,
    } = useRadioGroupContext();

    const itemDisabled = disabled || radioGroupDisabled;

    return (
      <div
        className={cn(
          "tw-grid tw-grid-cols-[auto_1fr] tw-items-center tw-space-x-16",
          {
            "tw-py-20 tw-px-16 tw-rounded-4 tw-border tw-border-gray-400":
              variant === "box",
            "tw-border-gray-900":
              variant === "box" && value === radioGroupValue,
            "tw-pointer-events-none tw-border-gray-400": itemDisabled,
            "tw-border-red-800": !!radioGroupErrorMsg,
          },
          className,
        )}
        onMouseDown={(e) => {
          if (variant === "box") {
            if (!allowEventBubbling) {
              e.preventDefault();
              e.stopPropagation();
            }
            onValueChange?.(value);
          }
        }}
      >
        <RadioGroupPrimitive.Item
          ref={ref || itemRef}
          className={cn(
            "tw-aspect-square tw-h-16 tw-w-16 tw-rounded-circular tw-border tw-border-gray-900 tw-text-gray-900 tw-ring-base-white focus:tw-outline-none focus-visible:tw-ring-8 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-8 disabled:tw-pointer-events-none radio-group-item",
            {
              "tw-pointer-events-none tw-border-gray-400": itemDisabled,
            },
            className,
          )}
          value={value}
          disabled={disabled}
          {...props}
        >
          <RadioGroupPrimitive.Indicator className="tw-flex tw-items-center tw-justify-center">
            <RadioCircle disabled={itemDisabled} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>

        <div>
          <Label
            htmlFor={props.id}
            className={cn("tw-body-2-m radio-group-item-text", {
              "tw-text-gray-500": itemDisabled,
            })}
          >
            {label}
          </Label>
        </div>

        <div />

        {note && (
          <div>
            <Label
              htmlFor={props.id}
              className={cn(
                "tw-body-1-long tw-text-gray-600 radio-group-item-text",
                {
                  "tw-text-gray-500": itemDisabled,
                },
              )}
            >
              {note}
            </Label>
          </div>
        )}
      </div>
    );
  },
);
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
